<!-- src/views/patients/PatientForm.vue -->
<template>
  <div class="patient-form">
    <!-- Back button and page title -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <router-link :to="returnPath" class="btn btn-outline-primary">
        <i class="bi bi-arrow-left me-2"></i>Volver
      </router-link>
      <h1>{{ isEditMode ? 'Editar Paciente' : 'Nuevo Paciente' }}</h1>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Form content -->
    <div v-else class="row">
      <div class="col-md-8 mx-auto">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="submitForm">
              <!-- Alert for errors -->
              <div v-if="error" class="alert alert-danger mb-4">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                {{ error }}
              </div>

              <div class="row g-3">
                <!-- First Name -->
                <div class="col-md-6">
                  <label for="firstName" class="form-label">Nombre(s) *</label>
                  <input type="text" class="form-control" id="firstName" v-model="form.first_name" required>
                  <div v-if="validationErrors.first_name" class="text-danger mt-1 small">
                    {{ validationErrors.first_name }}
                  </div>
                </div>

                <!-- Last Name -->
                <div class="col-md-6">
                  <label for="lastName" class="form-label">Apellido(s) *</label>
                  <input type="text" class="form-control" id="lastName" v-model="form.last_name" required>
                  <div v-if="validationErrors.last_name" class="text-danger mt-1 small">
                    {{ validationErrors.last_name }}
                  </div>
                </div>
                

                
                <!-- Gender -->
                <div class="col-md-6">
                  <label for="gender" class="form-label">Género</label>
                  <select class="form-select" id="gender" v-model="form.gender">
                    <option value="">Seleccionar</option>
                    <option value="masculino">Masculino</option>
                    <option value="femenino">Femenino</option>
                  </select>
                </div>

                <!-- Identity Card -->
                <div class="col-md-6">
                  <label for="identityCard" class="form-label">Cédula de Identidad</label>
                  <input type="text" class="form-control" id="identityCard" v-model="form.identity_card">
                </div>

                <!-- Phone -->
                <div class="col-md-6">
                  <label for="phone_number" class="form-label">Teléfono</label>
                  <input type="tel" class="form-control" id="phone_number" v-model="form.phone_number">
                  <div v-if="validationErrors.phone_number" class="text-danger mt-1 small">
                    {{ validationErrors.phone_number }}
                  </div>
                </div>

                <!-- Email -->
                <div class="col-md-6">
                  <label for="email" class="form-label">Correo Electrónico</label>
                  <input type="email" class="form-control" id="email" v-model="form.email">
                </div>

                <!-- Birth Date -->
                <div class="col-md-6">
                  <label for="birthDate" class="form-label">Fecha de nacimiento</label>
                  <input type="date" class="form-control" id="birthDate" v-model="form.fecha_nacimiento">
                  <div v-if="validationErrors.fecha_nacimiento" class="text-danger mt-1 small">
                    {{ validationErrors.fecha_nacimiento }}
                  </div>
                </div>
                
                <!-- Occupation -->
                <div class="col-md-6">
                  <label for="occupation" class="form-label">Ocupación</label>
                  <input type="text" class="form-control" id="occupation" v-model="form.occupation">
                </div>
                                <!-- Treatment Status -->
                                <div class="col-md-6">
                  <label for="treatment_status" class="form-label">Estado del Tratamiento</label>
                  <select class="form-select" id="treatment_status" v-model="form.treatment_status">
                    <option value="en_planificacion">En Planificación</option>
                    <option value="iniciado">Iniciado</option>
                    <option value="completado">Terminado</option>
                    <option value="en_pausa">En Pausa</option>
                  </select>
                </div>

                <!-- Address -->
                <div class="col-12">
                  <label for="address" class="form-label">Dirección</label>
                  <textarea class="form-control" id="address" v-model="form.address" rows="3"></textarea>
                </div>

                <!-- Referred By -->
                <div class="col-12">
                  <label for="referredBy" class="form-label">Referido por</label>
                  <input type="text" class="form-control" id="referredBy" v-model="form.referred_by">
                </div>

                <!-- Patient Notes -->
                <div class="col-12">
                  <label for="patientNotes" class="form-label">Notas del Paciente</label>
                  <textarea class="form-control" id="patientNotes" v-model="form.patient_notes" rows="3"></textarea>
                </div>

                <!-- Form actions -->
                <div class="col-12 mt-4">
                  <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-outline-secondary me-2" @click="resetForm">
                      Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary" :disabled="submitting">
                      <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ isEditMode ? 'Guardar cambios' : 'Guardar paciente' }}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <!-- Delete patient button (Edit mode only) -->
            <div v-if="isEditMode" class="mt-4 pt-4 border-top">
              <h5 class="text-danger mb-3">Zona de peligro</h5>
              <p class="text-muted mb-3">
                Al eliminar el paciente, se borrarán permanentemente todos sus datos, incluyendo el historial clínico,
                citas y odontogramas. Esta acción no se puede deshacer.
              </p>
              <button type="button" class="btn btn-outline-danger" @click="showDeleteConfirmation = true">
                <i class="bi bi-trash me-2"></i>Eliminar paciente
              </button>

              <!-- Delete confirmation modal -->
              <div class="modal fade" :class="{ show: showDeleteConfirmation, 'd-block': showDeleteConfirmation }"
                tabindex="-1" role="dialog" aria-hidden="true">
                <div class="modal-dialog">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title">Confirmar eliminación</h5>
                      <button type="button" class="btn-close" @click="showDeleteConfirmation = false"
                        aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                      <p>¿Estás seguro que deseas eliminar al paciente <strong>{{ form.first_name }} {{ form.last_name
                          }}</strong>?</p>
                      <p class="text-danger">Esta acción no se puede deshacer.</p>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary"
                        @click="showDeleteConfirmation = false">Cancelar</button>
                      <button type="button" class="btn btn-danger" @click="deletePatient" :disabled="deleting">
                        <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status"
                          aria-hidden="true"></span>
                        Eliminar paciente
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Modal backdrop -->
              <div v-if="showDeleteConfirmation" class="modal-backdrop fade show"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { patientService } from '@/services/api';

export default {
  name: 'PatientForm',
  data() {
    return {
      form: {
        first_name: '',
        last_name: '',
        treatment_status: 'en_planificacion', // Default value
        gender: '',
        identity_card: '',
        phone_number: '',
        email: '',
        fecha_nacimiento: '',
        occupation: '',
        address: '',
        referred_by: '',
        patient_notes: ''
        // budget removed as it's now part of TreatmentPlan
      },
      originalForm: {},
      validationErrors: {},
      loading: false,
      submitting: false,
      deleting: false,
      error: null,
      showDeleteConfirmation: false
    };
  },
  computed: {
    isEditMode() {
      return this.$route.path.includes('/edit');
    },
    patientId() {
      return this.$route.params.id;
    },
    returnPath() {
      // If we're in edit mode, return to patient detail page
      if (this.isEditMode) {
        return `/patients/${this.patientId}`;
      }
      // Otherwise, return to patients list
      return '/patients';
    }
  },
  async created() {
    if (this.isEditMode) {
      this.loading = true;
      try {
        await this.fetchPatient();
      } catch (error) {
        console.error('Error loading patient:', error);
        this.error = 'No se pudo cargar la información del paciente. Por favor, intenta de nuevo.';
      } finally {
        this.loading = false;
      }
    }
  },
  methods: {
    async fetchPatient() {
      const response = await patientService.get(this.patientId);
      this.form = { ...response.data };
      
      // Make sure treatment_status has a value if it's not set
      if (!this.form.treatment_status) {
        this.form.treatment_status = 'en_planificacion';
      }
      
      this.originalForm = { ...this.form };
    },
    validateForm() {
      this.validationErrors = {};
      let isValid = true;

      // First name validation
      if (!this.form.first_name.trim()) {
        this.validationErrors.first_name = 'El nombre es obligatorio';
        isValid = false;
      }

      // Last name validation
      if (!this.form.last_name.trim()) {
        this.validationErrors.last_name = 'El apellido es obligatorio';
        isValid = false;
      }

      // Phone validation (optional but must be valid if provided)
      if (this.form.phone_number && !/^\+?[0-9\s-]{7,15}$/.test(this.form.phone_number.trim())) {
        this.validationErrors.phone_number = 'Por favor, introduce un número de teléfono válido';
        isValid = false;
      }

      // Date validation (optional but must be valid if provided)
      if (this.form.fecha_nacimiento) {
        const birthDate = new Date(this.form.fecha_nacimiento);
        const today = new Date();

        if (isNaN(birthDate.getTime())) {
          this.validationErrors.fecha_nacimiento = 'Por favor, introduce una fecha válida';
          isValid = false;
        } else if (birthDate > today) {
          this.validationErrors.fecha_nacimiento = 'La fecha de nacimiento no puede ser en el futuro';
          isValid = false;
        }
      }

      // Email validation (if provided)
      if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) {
        this.validationErrors.email = 'Por favor, introduce un correo electrónico válido';
        isValid = false;
      }

      return isValid;
    },
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.submitting = true;
      this.error = null;

      try {
        if (this.isEditMode) {
          await patientService.update(this.patientId, this.form);
          // Navigate to patient detail page
          this.$router.push(`/patients/${this.patientId}`);
        } else {
          const response = await patientService.create(this.form);
          // Navigate to the new patient's detail page
          this.$router.push(`/patients/${response.data.id}`);
        }
      } catch (error) {
        console.error('Error saving patient:', error);
        this.error = 'Error al guardar los datos del paciente. Por favor, intenta de nuevo.';
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      if (this.isEditMode) {
        // In edit mode, reset to original values
        this.form = { ...this.originalForm };
      } else {
        // In create mode, clear the form
        this.form = {
          first_name: '',
          last_name: '',
          treatment_status: 'en_planificacion',
          gender: '',
          identity_card: '',
          phone_number: '',
          email: '',
          fecha_nacimiento: '',
          occupation: '',
          address: '',
          referred_by: '',
          patient_notes: ''
        };
      }

      this.validationErrors = {};
      this.error = null;
    },
    async deletePatient() {
      this.deleting = true;
      try {
        await patientService.delete(this.patientId);
        this.showDeleteConfirmation = false;
        // Show success message and redirect to patients list
        this.$router.push('/patients');
      } catch (error) {
        console.error('Error deleting patient:', error);
        this.error = 'No se pudo eliminar el paciente. Por favor, intenta de nuevo.';
        this.showDeleteConfirmation = false;
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>